// Updated DashboardLayout with Loader Bar
import { ReactNode, useState, useEffect } from 'react';
import {
  LayoutDashboard,
  CheckSquare,
  BookOpen,
  FlaskConical,
  BarChart3,
  Award,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  FileText,
  Map,
  Trophy,
  Search,
  Calendar,
  UserPlus,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { User } from '../../types';
import { cn } from '../ui/utils';
import GlobalSearch from './GlobalSearch';
import { tasksAPI, notificationsAPI, api } from '../../utils/api';
import NotificationTray from '../ui/NotificationTray';
import { useNotification } from '../context/NotificationContext';
import { useSearch } from '../context/SearchContext';
import PortalSwitcher from '../ui/PortalSwitcher';
import { routeConfig } from '../../routes';
import { useNavigate } from 'react-router-dom';

interface NavigationItem {
  id: string;
  label: string;
  icon: any;
  badge?: number;
}

interface DashboardLayoutProps {
  children: ReactNode;
  currentUser: User;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onLogout: () => void;
  unreadNotifications: number;
  onProfileClick?: () => void;
  // Enhanced design system props
  theme?: 'light' | 'dark' | 'auto';
  density?: 'compact' | 'comfortable' | 'spacious';
}


export default function DashboardLayout({
  children,
  currentUser,
  activeTab,
  onTabChange,
  onLogout,
  unreadNotifications,
  onProfileClick,
  theme = 'light',
  density = 'comfortable'
}: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [awaitingHrVerification, setAwaitingHrVerification] = useState(false);
  const navigate = useNavigate();

  // Soft banner: joiner finished their answers, management still pending
  useEffect(() => {
    const checkAwaitingHr = async () => {
      if (currentUser.role !== 'joiner') {
        setAwaitingHrVerification(false);
        return;
      }
      try {
        const response = await api.get('/onboarding-verification');
        const checklist = Array.isArray(response?.data) ? response.data : [];
        if (checklist.length === 0) {
          setAwaitingHrVerification(false);
          return;
        }
        const mandatoryItems = checklist.filter(
          (item: any) => item?.mandatory !== false && item?.phase !== 'DEMO_SCHEDULE'
        );
        const joinerDone = mandatoryItems.every((item: any) => item?.joinerDecision === 'YES');
        const hrPending = mandatoryItems.some(
          (item: any) => item?.hrDecision === 'PENDING' || item?.hrDecision !== 'YES'
        );
        setAwaitingHrVerification(joinerDone && hrPending);
      } catch {
        setAwaitingHrVerification(false);
      }
    };
    checkAwaitingHr();
  }, [currentUser.role, activeTab]);

  // Density configuration
  const densityConfig = {
    compact: {
      sidebarWidth: 'w-56', // 224px
      headerHeight: 'h-12', // 48px
      navPadding: 'px-2 py-1.5',
      navItemPadding: 'px-2.5 py-1.5',
      contentPadding: 'p-3 lg:p-4',
      iconSize: 'h-4 w-4',
      avatarSize: 'w-7 h-7',
      fontSize: 'text-sm',
      spacing: 'gap-1'
    },
    comfortable: {
      sidebarWidth: 'w-64', // 256px
      headerHeight: 'h-14', // 56px
      navPadding: 'px-2',
      navItemPadding: 'px-3.5 py-2.5',
      contentPadding: 'p-4 lg:p-5',
      iconSize: 'h-5 w-5',
      avatarSize: 'w-8 h-8',
      fontSize: 'text-base',
      spacing: 'gap-2'
    },
    spacious: {
      sidebarWidth: 'w-72', // 288px
      headerHeight: 'h-16', // 64px
      navPadding: 'px-3',
      navItemPadding: 'px-4 py-3',
      contentPadding: 'p-5 lg:p-6',
      iconSize: 'h-6 w-6',
      avatarSize: 'w-10 h-10',
      fontSize: 'text-lg',
      spacing: 'gap-3'
    }
  };

  const currentDensity = densityConfig[density];

  // Use global search context
  const { searchQuery, setSearchQuery, setCurrentPage } = useSearch();
  const [isLoading, setIsLoading] = useState(false);

  // Get notifications from context
  const {
    notifications,
    setNotifications,
    setUnreadCount,
    fetchNotifications,
  } = useNotification();

  // Handle tab change without reload - just update the route
  const handleTabClick = (tab: string) => {
    onTabChange(tab);
    setCurrentPage(tab); // Update search context with current page

    // Refresh pending reviews count when navigating away from reviews tab
    if (currentUser.role === 'buddy' && tab !== 'reviews') {
      fetchPendingReviewsManually();
    }
  };

  // Manual refresh function for pending reviews
  const fetchPendingReviewsManually = async () => {
    if (currentUser.role === 'buddy') {
      try {
        const response = await tasksAPI.getBuddyAssignments();
        if (response.success && response.data) {
          const { tasksByJoiner, tasks } = response.data;
          let pendingCount = 0;

          if (tasksByJoiner) {
            Object.values(tasksByJoiner).forEach((joinerData: any) => {
              if (joinerData.tasks) {
                const completedTasks = joinerData.tasks.filter((task: any) =>
                  (task.status === 'COMPLETED' || task.status === 'SUBMITTED') &&
                  task.status !== 'REVIEWED'
                );
                pendingCount += completedTasks.length;
              }
            });
          }

          if (pendingCount === 0 && tasks && Array.isArray(tasks)) {
            const completedTasks = tasks.filter((task: any) =>
              (task.status === 'COMPLETED' || task.status === 'SUBMITTED') &&
              task.status !== 'REVIEWED'
            );
            pendingCount = completedTasks.length;
          }

          console.log('🔔 Manual refresh - Pending reviews count:', pendingCount);
          setPendingReviews(pendingCount);
        }
      } catch (error) {
        console.error('Error fetching pending reviews:', error);
      }
    }
  };

  // Fetch pending reviews
  useEffect(() => {
    const fetchPendingReviews = async () => {
      if (currentUser.role === 'buddy') {
        try {
          const response = await tasksAPI.getBuddyAssignments();
          if (response.success && response.data) {
            const { tasksByJoiner, tasks } = response.data;
            let pendingCount = 0;

            // Method 1: Count from tasksByJoiner structure
            if (tasksByJoiner) {
              Object.values(tasksByJoiner).forEach((joinerData: any) => {
                if (joinerData.tasks) {
                  const completedTasks = joinerData.tasks.filter((task: any) =>
                    (task.status === 'COMPLETED' || task.status === 'SUBMITTED') &&
                    task.status !== 'REVIEWED'
                  );
                  pendingCount += completedTasks.length;
                }
              });
            }

            // Method 2: Count from flat tasks array (fallback)
            if (pendingCount === 0 && tasks && Array.isArray(tasks)) {
              const completedTasks = tasks.filter((task: any) =>
                (task.status === 'COMPLETED' || task.status === 'SUBMITTED') &&
                task.status !== 'REVIEWED'
              );
              pendingCount = completedTasks.length;
            }

            console.log('🔔 Pending reviews count:', pendingCount);
            setPendingReviews(pendingCount);
          }
        } catch (error) {
          console.error('Error fetching pending reviews:', error);
        }
      }
    };

    fetchPendingReviews();

    // Listen for custom event to refresh immediately
    const handleRefreshReviews = () => {
      console.log('🔄 Received refresh-pending-reviews event');
      fetchPendingReviews();
    };
    window.addEventListener('refresh-pending-reviews', handleRefreshReviews);

    return () => {
      window.removeEventListener('refresh-pending-reviews', handleRefreshReviews);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.role]); // Only run when user role changes, not on every tab change

  const getNavigationItems = (): NavigationItem[] => {
    const common = [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      // { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      // { id: 'labs', label: 'Labs', icon: FlaskConical },
      // { id: 'messages', label: 'Messages', icon: MessageCircle }
    ];

    // Admin navigation - check before team_role so admin keeps org-wide Reviews + View
    if (currentUser.role.toLowerCase() === 'admin') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'team-management', label: 'Team Management', icon: Settings },
        { id: 'performance-quarterly', label: 'Quarterly Reviews', icon: Sparkles },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'labs', label: 'Labs', icon: FlaskConical },
        { id: 'admin-reviews', label: 'Reviews', icon: FileText },
        { id: 'schedule-demo', label: 'Schedule Demo', icon: Calendar },
        { id: 'pre-onboarding', label: 'Pre-Onboarding', icon: UserPlus },
        { id: 'verification', label: 'Verification', icon: FileText },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
      ];
    }

    // Team Lead navigation (team_role based)
    if (currentUser.team_role === 'TEAM_LEAD') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'my-team', label: 'My Team', icon: Users },
        { id: 'performance-quarterly', label: 'Quarterly Reviews', icon: Sparkles },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'labs', label: 'Labs', icon: FlaskConical },
        { id: 'team-lead-reviews', label: 'Reviews', icon: FileText },
        { id: 'schedule-demo', label: 'Schedule Demo', icon: Calendar },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
      ];
    }

    // Full-time MEMBER with active performance track
    if (currentUser.team_role === 'MEMBER' && currentUser.hasFullTimeTrack) {
      return [
        { id: 'performance', label: 'My Performance', icon: LayoutDashboard },
        { id: 'performance-quarterly', label: 'Meetings', icon: Sparkles },
        { id: 'performance-goals', label: 'Goals', icon: BarChart3 },
        { id: 'performance-feedback', label: 'Feedback', icon: FileText },
      ];
    }

    if (currentUser.role === 'joiner') {
      return [
        ...common,
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'labs', label: 'Labs', icon: FlaskConical },

        { id: 'progress', label: 'My Progress', icon: BarChart3 },
        { id: 'task-report', label: 'Task Report', icon: FileText },
        { id: 'verification', label: 'Verification', icon: FileText },
        { id: 'schedule-demo', label: 'My Demos', icon: Calendar },

        { id: 'submit-documents', label: 'My Documents', icon: FileText },

      ];
    }

    if (currentUser.role === 'buddy') {
      return [
        ...common,
        { id: 'labs', label: 'Labs', icon: FlaskConical },
        { id: 'joiners', label: 'My Joiners', icon: Users },
        // { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        {
          id: 'reviews',
          label: 'Reviews',
          icon: FileText,
          badge: pendingReviews > 0 ? pendingReviews : undefined
        },
        { id: 'schedule-demo', label: 'Schedule Demo', icon: Calendar },
        { id: 'submit-documents', label: 'Joiner Documents', icon: FileText },
      ];
    }

    if (currentUser.role.toLowerCase() === 'hr') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'team-management', label: 'Team Management', icon: Settings },
        { id: 'performance-quarterly', label: 'Quarterly Reviews', icon: Sparkles },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'labs', label: 'Labs', icon: FlaskConical },
        { id: 'hr-reviews', label: 'Reviews', icon: FileText },
        { id: 'pre-onboarding', label: 'Pre-Onboarding', icon: UserPlus },
        { id: 'verification', label: 'Verification', icon: FileText },
        { id: 'schedule-demo', label: 'Schedule Demo', icon: Calendar },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
      ];
    }


    // Manager navigation - same as HR with same accessibility rules
    if (currentUser.role.toLowerCase() === 'manager') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        // { id: 'joiners', label: 'All Joiners', icon: Users },
        { id: 'team-management', label: 'Team Management', icon: Settings },
        // { id: 'teams-hrms', label: 'Teams', icon: Users },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'labs', label: 'Labs', icon: FlaskConical },
        { id: 'manager-reviews', label: 'Reviews', icon: FileText },
        { id: 'analytics', label: 'Analytics', icon: BarChart3 },
        // { id: 'certificates', label: 'Certificates', icon: Award },
        // { id: 'messages', label: 'Messages', icon: MessageCircle }
      ];
    }

    return common;
  };

  const navigationItems = getNavigationItems();

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar - Full height like HRMS */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex w-64 flex-col">
          <div className="flex flex-grow flex-col overflow-x-hidden overflow-y-auto border-r border-gray-200 bg-white pt-4 pb-3">
            {/* Portal Switcher */}
            <div className="flex items-center flex-shrink-0 px-4">
              <div className="w-full">
                <PortalSwitcher currentPortal="onboarding" />
              </div>
            </div>

            {/* Navigation */}
            <nav className="mt-2 flex-1 space-y-1 px-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <button
                    key={item.id}
                    className={cn(
                      "group w-full flex items-center rounded-md px-3.5 py-2.5 text-base font-medium transition-colors duration-200",
                      isActive
                        ? "bg-blue-50 text-blue-600 border-l-2 border-blue-600"
                        : "text-gray-600 hover:bg-blue-50 hover:text-blue-600"
                    )}
                    onClick={() => {
                      handleTabClick(item.id);
                      setSidebarOpen(false);
                    }}
                  >
                    <Icon className={cn(
                      "mr-3 h-5 w-5 flex-shrink-0",
                      isActive ? "text-blue-500" : "text-gray-400 group-hover:text-gray-500"
                    )} />
                    <span className="truncate flex-1 text-left">{item.label}</span>
                    {item.id === 'reviews' && item.badge && (
                      <Badge variant="destructive" className="ml-2 text-xs animate-pulse bg-red-500">
                        {item.badge}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="relative z-30 flex h-14 flex-shrink-0 bg-white shadow-sm border-b border-gray-200">
          {/* Mobile menu button */}
          <button
            type="button"
            className="px-3 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>

          <div className="flex flex-1 min-w-0 items-center justify-between px-3 md:px-4">
            {/* Search */}
            <div className="flex flex-1 min-w-0">
              <form className="flex w-full min-w-0 md:ml-0" onSubmit={(e) => { e.preventDefault(); }}>
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <div className="relative w-full min-w-0 text-gray-400 focus-within:text-gray-600">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                    <Search className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <input
                    id="search-field"
                    className="block h-full w-full border-transparent py-1.5 pl-7 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:border-transparent focus:outline-none focus:placeholder-gray-400 focus:ring-0"
                    placeholder="Search tasks, content, or anything..."
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </form>
            </div>

            <div className="ml-2 flex flex-shrink-0 items-center gap-2 md:ml-3 md:gap-3">
              <NotificationTray
                notifications={notifications}
                unreadCount={unreadNotifications}
                onMarkAsRead={async (id: string) => {
                  try {
                    const res = await notificationsAPI.markAsRead(id);
                    if (res.success) {
                      setNotifications((prev) =>
                        prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
                      );
                      setUnreadCount((prev) => Math.max(prev - 1, 0));
                    }
                  } catch (error) {
                    console.error('Failed to mark as read:', error);
                  }
                }}
                onDelete={async (id: string) => {
                  try {
                    await notificationsAPI.deleteNotification(id);
                    setNotifications((prev) => {
                      const updated = prev.filter((n) => n.id !== id);
                      const unread = updated.filter((n) => !n.isRead).length;
                      setUnreadCount(unread);
                      return updated;
                    });
                  } catch (error) {
                    console.error('Failed to delete notification:', error);
                  }
                }}
                onViewAll={() => handleTabClick('notifications')}
                onMarkAllAsRead={async () => {
                  try {
                    await notificationsAPI.markAllAsRead();
                    await fetchNotifications();
                  } catch (error) {
                    console.error('Failed to mark all as read:', error);
                  }
                }}
              />

              <Button
                variant="ghost"
                size="icon"
                className="hidden sm:inline-flex"
                onClick={() => onProfileClick ? onProfileClick() : handleTabClick('profile')}
                title="Profile Settings"
              >
                <Settings className="w-5 h-5" />
              </Button>

              <div className="flex items-center gap-2 pl-2 border-l">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={currentUser.avatar} />
                  <AvatarFallback>{currentUser.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="hidden md:block">
                  <p className="text-sm text-slate-800">{currentUser.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{currentUser.role}</p>
                </div>
              </div>

              <Button variant="ghost" size="icon" onClick={onLogout} title="Logout">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 min-w-0 overflow-x-auto overflow-y-auto">
          {awaitingHrVerification && (
            <div className="mx-4 mt-4 lg:mx-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2">
              <span>
                Your verification answers are submitted. Waiting for management review — you can keep using the portal.
              </span>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-900 hover:bg-amber-100"
                onClick={() => navigate(routeConfig.verification)}
              >
                View status
              </Button>
            </div>
          )}
          <div className="min-w-0 p-4 lg:p-5">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl lg:hidden transition-transform",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full overflow-x-hidden">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 p-3">
            <div className="mr-3 flex-1">
              <PortalSwitcher currentPortal="onboarding" />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-500 flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  className={cn(
                    "group w-full flex items-center rounded-md px-3.5 py-2.5 text-base font-medium transition-colors duration-200",
                    isActive
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                  onClick={() => {
                    handleTabClick(item.id);
                    setSidebarOpen(false);
                  }}
                >
                  <Icon className={cn(
                    "mr-3 h-5 w-5 flex-shrink-0",
                    isActive ? "text-blue-500" : "text-gray-400 group-hover:text-gray-500"
                  )} />
                  <span className="truncate">{item.label}</span>
                  {item.id === 'reviews' && item.badge && (
                    <Badge variant="destructive" className="ml-2 text-xs animate-pulse bg-red-500">
                      {item.badge}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Global Search */}
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

